'use client';

import { ReactNode, useState } from 'react';

import dynamic from 'next/dynamic';
import { css, cx } from 'styled-system/css';

// The quiz steps (@/rsc/quiz): requested on the "start" click only.
const AiReadinessQuizChunk = dynamic(() => import('./AiReadinessQuizChunk.client'), { ssr: false });

// quizCardShell.ts (dark glass card, corner glow, border).
const shellClass = css({
  border: '1px solid rgba(255,255,255,0.07)',
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-40%',
    right: '-10%',
    width: '360px',
    height: '360px',
    borderRadius: 'full',
    background: 'radial-gradient(circle, rgba(147,51,234,0.35), transparent 70%)',
    filter: 'blur(40px)',
    pointerEvents: 'none',
  },
});

const wrapperClass = css({ my: { base: 10, md: 14 }, position: 'relative', isolation: 'isolate' });

// QuizIntroCard: Chakra `Button variant="unstyled"` + its props/sx.
const cardClass = css({
  appearance: 'none',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',
  borderRadius: 'md',
  fontWeight: 'semibold',
  fontSize: 'md',
  lineHeight: 'inherit',
  color: 'inherit',
  m: 0,
  minH: 10,
  minW: 10,
  _focusVisible: { boxShadow: 'outline' },
  display: 'flex',
  flexDirection: { base: 'column', md: 'row' },
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: { base: 8, md: 12 },
  w: 'full',
  h: 'auto',
  whiteSpace: 'normal',
  textAlign: { base: 'center', md: 'left' },
  p: { base: 8, md: '56px' },
  bg: 'surface.500',
  overflow: 'hidden',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  // Only border/shadow/transform animate on hover — the card's own bg never changes.
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      borderColor: 'rgba(147,51,234,0.4)',
      transform: 'translateY(-4px)',
      boxShadow: '0 32px 80px rgba(147,51,234,0.35)',
      '& .quizArrow': { w: '26px' },
    },
  },
});

const expandedClass = css({ bg: 'surface.500', overflow: 'hidden', p: { base: 8, md: '56px' } });

type Props = {
  locale: string;
  /** The server-rendered intro card content. */
  children: ReactNode;
};

/** The banner's only interactive part: intro card button → in-place quiz (lazy chunk). */
export const AiReadinessQuizStart = ({ locale, children }: Props) => {
  const [isStarted, setIsStarted] = useState(false);

  if (!isStarted) {
    return (
      <div className={wrapperClass}>
        <button type="button" onClick={() => setIsStarted(true)} className={cx(shellClass, cardClass)}>
          {children}
        </button>
      </div>
    );
  }

  return (
    <div className={cx(wrapperClass, shellClass, expandedClass)}>
      <div className={css({ position: 'relative', zIndex: 1 })}>
        <AiReadinessQuizChunk locale={locale} />
      </div>
    </div>
  );
};
